import React, { useState } from 'react';
import { X, Clock, RotateCcw, Eye, ChevronLeft } from 'lucide-react';
import { ProjectVersion } from '../utils/projectStorage';
import { Project } from '../types';

interface VersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  versions: ProjectVersion[];
  onRestore: (version: ProjectVersion) => void;
  onDeleteVersion?: (versionId: string) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  isOpen,
  onClose,
  project,
  versions,
  onRestore,
  onDeleteVersion
}) => {
  const [selectedVersion, setSelectedVersion] = useState<ProjectVersion | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleRestore = (version: ProjectVersion) => {
    if (window.confirm(`Restore to "${version.name || 'this version'}"? This will replace your current canvas.`)) {
      onRestore(version);
      onClose();
    }
  };

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl w-[700px] max-w-[90vw] max-h-[85vh] flex flex-col border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-black/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-black">Version History</h2>
              <p className="text-sm text-gray-600 mt-1">{project.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Versions List */}
        <div className="flex-1 overflow-y-auto p-6">
          {versions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">No versions yet</p>
              <p className="text-sm mt-2">Versions are created automatically when you make changes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions
                .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                .map((version) => (
                  <div
                    key={version.id}
                    className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                      selectedVersion?.id === version.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedVersion(version)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-black">
                            {version.name || `Version ${versions.indexOf(version) + 1}`}
                          </h3>
                          {version.description && (
                            <span className="text-xs text-gray-500">• {version.description}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {formatDate(version.created)}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{version.nodes.length} components</span>
                          <span>{version.edges.length} connections</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(version);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Restore this version"
                        >
                          <RotateCcw size={16} />
                        </button>
                        {onDeleteVersion && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Delete this version?')) {
                                onDeleteVersion(version.id);
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete version"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-black/10 bg-gray-50 text-xs text-gray-500 text-center">
          {versions.length} version{versions.length !== 1 ? 's' : ''} stored (max 20 per project)
        </div>
      </div>
    </div>
  );
};

export default VersionHistory;
